import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Mail, Send, Download, Filter, Loader2, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = ['confirmed', 'pending', 'unsubscribed', 'all'];
const LANGUAGES = ['all', 'sk', 'cs', 'de', 'en', 'ru'];

const AdminNewsletter = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [status, setStatus] = useState('confirmed');
  const [language, setLanguage] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [subs, setSubs] = useState([]);
  const [counts, setCounts] = useState({ confirmed: 0, pending: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(true);

  // Broadcast composer
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [bcLanguage, setBcLanguage] = useState('all');
  const [sending, setSending] = useState(false);
  const [bcResult, setBcResult] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const url = status === 'all'
        ? `${API_URL}/api/admin/newsletter/subscribers`
        : `${API_URL}/api/admin/newsletter/subscribers?status=${status}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSubs(data.subscribers || []);
        setCounts(data.counts || { confirmed: 0, pending: 0, unsubscribed: 0 });
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [status]);

  const filtered = useMemo(() => {
    return subs.filter((s) => {
      if (language !== 'all' && s.language !== language) return false;
      if (dateFrom && (s.created_at || '') < dateFrom) return false;
      if (dateTo && (s.created_at || '') > `${dateTo}T23:59:59`) return false;
      return true;
    });
  }, [subs, language, dateFrom, dateTo]);

  const exportCsv = () => {
    const rows = [['email', 'status', 'language', 'created_at', 'confirmed_at', 'source']];
    filtered.forEach((s) => rows.push([
      s.email || '',
      s.status || '',
      s.language || '',
      s.created_at || '',
      s.confirmed_at || '',
      s.source || '',
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-${status}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sendBroadcast = async () => {
    if (!subject.trim() || !body.trim()) return;
    if (!window.confirm(t('admin.newsletter.confirm_send', `Naozaj odoslať tento email${bcLanguage !== 'all' ? ` (${bcLanguage.toUpperCase()})` : ''}?`))) return;
    setSending(true);
    setBcResult(null);
    try {
      const payload = { subject, html: body };
      if (bcLanguage !== 'all') payload.language = bcLanguage;
      const res = await fetch(`${API_URL}/api/admin/newsletter/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        setBcResult({ ok: false, msg: data.detail || 'Chyba odosielania' });
      } else {
        setBcResult({ ok: true, sent: data.sent || 0, failed: data.failed || 0 });
        setSubject('');
        setBody('');
      }
    } catch (e) {
      setBcResult({ ok: false, msg: String(e) });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-newsletter-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Mail className="h-6 w-6 text-orange-500" />
          {t('admin.newsletter.title', 'Newsletter')}
        </h1>
        <button
          onClick={fetchData}
          className="px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-50 inline-flex items-center gap-1"
          data-testid="newsletter-refresh-btn"
        >
          <RefreshCw className="h-4 w-4" /> {t('admin.refresh', 'Obnoviť')}
        </button>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white p-4 rounded-lg border" data-testid="newsletter-count-confirmed">
          <div className="text-xs text-gray-500 uppercase">Confirmed</div>
          <div className="text-2xl font-bold text-green-600">{counts.confirmed || 0}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border" data-testid="newsletter-count-pending">
          <div className="text-xs text-gray-500 uppercase">Pending</div>
          <div className="text-2xl font-bold text-orange-500">{counts.pending || 0}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border" data-testid="newsletter-count-unsubscribed">
          <div className="text-xs text-gray-500 uppercase">Unsubscribed</div>
          <div className="text-2xl font-bold text-gray-500">{counts.unsubscribed || 0}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-xs text-gray-500 uppercase">Total</div>
          <div className="text-2xl font-bold">
            {(counts.confirmed || 0) + (counts.pending || 0) + (counts.unsubscribed || 0)}
          </div>
        </div>
      </div>

      {/* Subscribers section */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <div className="flex gap-1">
              {STATUS_TABS.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 text-sm rounded ${status === s ? 'bg-orange-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                  data-testid={`newsletter-tab-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('admin.newsletter.language', 'Jazyk')}</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="px-3 py-1.5 border rounded text-sm"
              data-testid="newsletter-language-filter"
            >
              {LANGUAGES.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('admin.newsletter.from', 'Od')}</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-1.5 border rounded text-sm" data-testid="newsletter-date-from" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('admin.newsletter.to', 'Do')}</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-1.5 border rounded text-sm" data-testid="newsletter-date-to" />
          </div>
          <div className="flex-1" />
          <div className="text-sm text-gray-500 self-center">
            <Filter className="h-4 w-4 inline mr-1" /> {filtered.length} / {subs.length}
          </div>
          <button
            onClick={exportCsv}
            disabled={!filtered.length}
            className="px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="newsletter-export-csv"
          >
            <Download className="h-4 w-4" /> CSV
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid="newsletter-empty">
            {t('admin.newsletter.empty', 'Žiadni odberatelia.')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Lang</th>
                  <th className="px-4 py-2 font-medium">Created</th>
                  <th className="px-4 py-2 font-medium">Confirmed</th>
                  <th className="px-4 py-2 font-medium">Source</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, idx) => (
                  <tr key={s.email + idx} className="border-t" data-testid="newsletter-sub-row">
                    <td className="px-4 py-2 font-mono text-xs">{s.email}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                        s.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                        s.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-200 text-gray-700'
                      }`}>{s.status}</span>
                    </td>
                    <td className="px-4 py-2 uppercase text-xs">{s.language || '—'}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{(s.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{(s.confirmed_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-4 py-2 text-xs">{s.source || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Broadcast composer */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h2 className="font-bold flex items-center gap-2">
            <Send className="h-4 w-4 text-orange-500" />
            {t('admin.newsletter.compose', 'Odoslať newsletter')}
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            {t('admin.newsletter.compose_help', 'Odošle sa len potvrdeným odberateľom. Unsubscribe link sa pridáva automaticky.')}
          </p>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border rounded"
                placeholder={t('admin.newsletter.subject_ph', 'Akcia: -10% na všetky fólie')}
                data-testid="newsletter-subject"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('admin.newsletter.target_lang', 'Cieľový jazyk')}</label>
              <select
                value={bcLanguage}
                onChange={(e) => setBcLanguage(e.target.value)}
                className="w-full px-3 py-2 border rounded"
                data-testid="newsletter-broadcast-language"
              >
                <option value="all">All ({(counts.confirmed || 0)})</option>
                {['sk', 'cs', 'de', 'en', 'ru'].map((l) => (
                  <option key={l} value={l}>{l.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">HTML body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 border rounded font-mono text-xs"
              placeholder={t('admin.newsletter.body_ph', '<h2>Akcia tohto týždňa</h2><p>Použite kód <strong>SUMMER10</strong>...</p>')}
              data-testid="newsletter-body"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={sendBroadcast}
              disabled={sending || !subject.trim() || !body.trim()}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="newsletter-send-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {t('admin.newsletter.send', 'Odoslať')}
            </button>
            {bcResult && (
              bcResult.ok ? (
                <span className="text-green-600 text-sm" data-testid="newsletter-send-result">
                  {t('admin.newsletter.sent_count', 'Odoslané')}: {bcResult.sent} · {t('admin.newsletter.failed_count', 'Chyba')}: {bcResult.failed}
                </span>
              ) : (
                <span className="text-red-600 text-sm">{bcResult.msg}</span>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNewsletter;
